import debounce from 'lodash-es/debounce';
import { useAppStore } from '@/stores/app';

const MIN_QUERY_LENGTH = 3;
const REINDEX_DEBOUNCE_MS = 200;

// Check CSS Custom Highlight API support
const supportsHighlightAPI = typeof globalThis !== 'undefined' && typeof CSS !== 'undefined' && 'highlights' in CSS;

export type PageSearchReturn = ReturnType<typeof usePageSearch>;
export const PAGE_SEARCH_KEY: InjectionKey<PageSearchReturn> = Symbol('pageSearch');

export function usePageSearch() {
    const appStore = useAppStore();

    const matches = ref<Range[]>([]);
    const currentIndex = ref(-1);
    const totalMatches = ref(0);

    let mutationObserver: MutationObserver | null = null;
    let isReindexing = false;

    // ── Core search logic ─────────────────────────────────────────────

    function getSearchContainer(): Element | null {
        // Search entire app — covers main content, draggable windows, bet logs, etc.
        return document.querySelector('#mainId');
    }

    function performSearch(query: string, options?: { skipScroll?: boolean }) {
        clearHighlights();

        if (!query || query.length < MIN_QUERY_LENGTH) {
            matches.value = [];
            totalMatches.value = 0;
            currentIndex.value = -1;
            return;
        }

        const container = getSearchContainer();
        if (!container) return;

        const lowerQuery = query.toLowerCase();
        const foundRanges: Range[] = [];

        // TreeWalker to iterate all text nodes
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
            acceptNode(node: Node) {
                const parent = node.parentElement;
                if (!parent) return NodeFilter.FILTER_REJECT;

                // Skip the search bar itself, navigation drawers, app bar menu
                if (parent.closest('.app-expandable-search, .v-navigation-drawer, #menu')) return NodeFilter.FILTER_REJECT;

                // Skip inputs, selects, textareas
                if (parent.closest('input, select, textarea')) return NodeFilter.FILTER_REJECT;

                // Skip hidden elements
                const style = getComputedStyle(parent);
                if (style.display === 'none' || style.visibility === 'hidden') return NodeFilter.FILTER_REJECT;

                // Skip empty text nodes
                if (!node.textContent || node.textContent.trim().length === 0) return NodeFilter.FILTER_REJECT;

                return NodeFilter.FILTER_ACCEPT;
            },
        });

        while (walker.nextNode()) {
            const textNode = walker.currentNode as Text;
            const text = textNode.textContent?.toLowerCase() || '';
            let pos = 0;

            while ((pos = text.indexOf(lowerQuery, pos)) !== -1) {
                try {
                    const range = new Range();
                    range.setStart(textNode, pos);
                    range.setEnd(textNode, pos + query.length);
                    foundRanges.push(range);
                } catch {
                    // Range might fail if node was modified — skip
                }
                pos += query.length;
            }
        }

        matches.value = foundRanges;
        totalMatches.value = foundRanges.length;
        currentIndex.value = foundRanges.length > 0 ? 0 : -1;

        applyHighlights();

        // Only scroll on user-initiated search (not on auto-reindex)
        if (foundRanges.length > 0 && !options?.skipScroll) {
            scrollToCurrent();
        }
    }

    // ── Highlight application ──────────────────────────────────────────

    function applyHighlights() {
        if (!supportsHighlightAPI) return;

        // Clear existing highlights
        (CSS as any).highlights.delete('search-results');
        (CSS as any).highlights.delete('search-current');

        if (matches.value.length === 0) return;

        try {
            // Highlight all matches (yellow)
            const allHighlight = new (globalThis as any).Highlight(...matches.value);
            (CSS as any).highlights.set('search-results', allHighlight);

            // Highlight current match (orange)
            if (currentIndex.value >= 0 && currentIndex.value < matches.value.length) {
                const currentHighlight = new (globalThis as any).Highlight(matches.value[currentIndex.value]);
                (CSS as any).highlights.set('search-current', currentHighlight);
            }
        } catch {
            // Ranges might be invalid if DOM changed — ignore silently
        }
    }

    function clearHighlights() {
        if (!supportsHighlightAPI) return;

        try {
            (CSS as any).highlights.delete('search-results');
            (CSS as any).highlights.delete('search-current');
        } catch {
            // Ignore
        }
    }

    // ── Navigation ─────────────────────────────────────────────────────

    function goToNext() {
        if (totalMatches.value === 0) return;
        currentIndex.value = (currentIndex.value + 1) % totalMatches.value;
        applyHighlights();
        scrollToCurrent();
    }

    function goToPrev() {
        if (totalMatches.value === 0) return;
        currentIndex.value = (currentIndex.value - 1 + totalMatches.value) % totalMatches.value;
        applyHighlights();
        scrollToCurrent();
    }

    function scrollToCurrent() {
        if (currentIndex.value < 0 || currentIndex.value >= matches.value.length) return;

        try {
            const range = matches.value[currentIndex.value];
            const parentEl = range.startContainer.parentElement;
            if (!parentEl) return;

            // Find the nearest scrollable ancestor
            const scrollable = findScrollableParent(parentEl);
            if (scrollable) {
                const rect = range.getBoundingClientRect();
                const scrollRect = scrollable.getBoundingClientRect();

                // Check if current match is outside visible area
                if (rect.top < scrollRect.top || rect.bottom > scrollRect.bottom) {
                    parentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            } else {
                parentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } catch {
            // Range might be stale
        }
    }

    function findScrollableParent(el: Element): Element | null {
        let current: Element | null = el;
        while (current) {
            const style = getComputedStyle(current);
            const overflow = style.overflow + style.overflowY;
            if (/(auto|scroll)/.test(overflow) && current.scrollHeight > current.clientHeight) {
                return current;
            }
            current = current.parentElement;
        }
        return null;
    }

    // ── Auto-reindex on DOM changes ────────────────────────────────────

    const debouncedReindex = debounce(() => {
        if (!appStore.searchActive || !appStore.searchQuery || isReindexing) return;

        isReindexing = true;

        // Save current position context for restoring after reindex
        const prevCurrentIndex = currentIndex.value;

        // skipScroll: true — don't auto-scroll when content updates
        performSearch(appStore.searchQuery, { skipScroll: true });

        // Try to restore position close to where user was
        if (prevCurrentIndex >= 0 && totalMatches.value > 0) {
            currentIndex.value = Math.min(prevCurrentIndex, totalMatches.value - 1);
            applyHighlights();
        }

        isReindexing = false;
    }, REINDEX_DEBOUNCE_MS);

    function startObserving() {
        const container = getSearchContainer();
        if (!container || mutationObserver) return;

        mutationObserver = new MutationObserver((mutations) => {
            // Only reindex if there are meaningful DOM changes
            const hasMeaningfulChange = mutations.some((m) => {
                // Skip mutations inside the search bar itself to prevent reindex loops
                const target = m.target as Node;
                const targetEl = target.nodeType === Node.TEXT_NODE ? target.parentElement : (target as Element);
                if (targetEl?.closest('.app-expandable-search')) return false;

                // Character data change (text updated)
                if (m.type === 'characterData') return true;
                // Child nodes added/removed
                if (m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0)) return true;
                return false;
            });

            if (hasMeaningfulChange) {
                debouncedReindex();
            }
        });

        mutationObserver.observe(container, {
            childList: true,
            subtree: true,
            characterData: true,
        });
    }

    function stopObserving() {
        if (mutationObserver) {
            mutationObserver.disconnect();
            mutationObserver = null;
        }
    }

    // ── Public methods ─────────────────────────────────────────────────

    function openSearch() {
        appStore.searchActive = true;
        // Start observing DOM for auto-reindex
        nextTick(() => {
            startObserving();
            if (appStore.searchQuery && appStore.searchQuery.length >= MIN_QUERY_LENGTH) {
                performSearch(appStore.searchQuery);
            }
        });
    }

    function closeSearch() {
        clearHighlights();
        stopObserving();
        appStore.searchQuery = '';
        appStore.searchActive = false;
        matches.value = [];
        totalMatches.value = 0;
        currentIndex.value = -1;
    }

    function updateQuery(query: string) {
        appStore.searchQuery = query;
        performSearch(query);
    }

    function reindex() {
        if (appStore.searchActive && appStore.searchQuery) {
            nextTick(() => {
                performSearch(appStore.searchQuery);
                // Re-start observer in case container changed (route change)
                stopObserving();
                startObserving();
            });
        }
    }

    // ── Lifecycle ──────────────────────────────────────────────────────

    // If search was already active (persisted across navigation), restart
    onMounted(() => {
        if (appStore.searchActive) {
            nextTick(() => {
                startObserving();
                if (appStore.searchQuery && appStore.searchQuery.length >= MIN_QUERY_LENGTH) {
                    // Double nextTick to ensure route component has rendered
                    nextTick(() => {
                        performSearch(appStore.searchQuery);
                    });
                }
            });
        }
    });

    onBeforeUnmount(() => {
        clearHighlights();
        stopObserving();
        debouncedReindex.cancel();
    });

    // Watch for search active state changes
    watch(
        () => appStore.searchActive,
        (active) => {
            if (active) {
                nextTick(() => startObserving());
            } else {
                clearHighlights();
                stopObserving();
            }
        }
    );

    return {
        // State
        currentIndex,
        totalMatches,
        supportsHighlightAPI,

        // Methods
        openSearch,
        closeSearch,
        updateQuery,
        goToNext,
        goToPrev,
        reindex,
        performSearch,
    };
}
